import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, FileText, Eye, Scale } from "lucide-react";

const TIERS = [
  { label: "Documented", color: "#5DD4B3", body: "Backed by a primary document or court record. Cited directly." },
  { label: "Reported", color: "#9FA8D4", body: "Reported by an established outlet with editorial oversight. Cited inline." },
  { label: "Alleged", color: "#E0A526", body: "Sworn testimony, court allegations, or credible witness claim. Clearly labeled as allegation." },
  { label: "Unverified", color: "#A39E93", body: "Surfaced but not yet corroborated. Held in editorial review — not published publicly." },
];

const RULES = [
  {
    icon: ShieldCheck,
    title: "No guilt by association",
    body: "Appearance in a flight log, a phone book, or a guest list is a documented connection — not a verdict. We never imply criminal conduct that has not been proven in court, and we label every record by what it actually shows.",
  },
  {
    icon: FileText,
    title: "Source-or-spike",
    body: "If a claim has no working public source, it does not get published. Every assertion in a public record on The Archive carries at least one verifiable citation. Submissions without sources are returned, not posted.",
  },
  {
    icon: Eye,
    title: "AI as assistant, not author",
    body: "We use language models to summarise, structure, and cross-check material. AI never originates a claim. Every fact is traced back to a human-verifiable document before it is shown.",
  },
  {
    icon: Scale,
    title: "Correctable by design",
    body: "Records are versioned. If we publish something inaccurate, we want to know. Corrections are accepted via the submission form and the record is updated — or removed — when the underlying source is shown to be wrong.",
  },
];

const EditorialStandards = () => (
  <div className="max-w-4xl mx-auto px-6 md:px-12 py-16" data-testid="editorial-page">
    <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-3">// EDITORIAL STANDARDS</div>
    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#E4DFD3] mb-5">
      How we decide what gets published.
    </h1>
    <p className="text-base text-[#A39E93] leading-relaxed mb-12 max-w-3xl">
      The Archive only works if you can trust the floor under it. These are the
      rules that govern what we publish, how we label it, and how mistakes get
      corrected.
    </p>

    <div className="grid md:grid-cols-2 gap-px bg-[#2D2A26] border border-[#2D2A26] mb-12">
      {RULES.map((r) => (
        <div key={r.title} className="bg-[#131313] p-7" data-testid={`editorial-rule-${r.title.toLowerCase().replace(/\s+/g, "-")}`}>
          <r.icon size={20} className="text-[#D92D20] mb-3" />
          <h3 className="font-heading text-xl text-[#E4DFD3] mb-2">{r.title}</h3>
          <p className="text-sm text-[#A39E93] leading-relaxed">{r.body}</p>
        </div>
      ))}
    </div>

    <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-3">// CONFIDENCE TIERS</div>
    <h2 className="font-display text-2xl sm:text-3xl text-[#E4DFD3] mb-4">
      Every claim is labeled, not levelled.
    </h2>
    <p className="text-sm text-[#A39E93] leading-relaxed mb-6 max-w-3xl">
      A rumor and a court filing are not the same thing — and we refuse to flatten
      them. Each record carries one of four tiers so you always know what kind of
      evidence you are looking at.
    </p>

    <div className="border border-[#2D2A26] bg-[#0F0F0F] divide-y divide-[#2D2A26] mb-12">
      {TIERS.map((t) => (
        <div key={t.label} className="flex items-start gap-4 px-5 py-4" data-testid={`tier-${t.label.toLowerCase()}`}>
          <span
            className="font-mono text-[10px] uppercase tracking-[0.3em] shrink-0 w-28"
            style={{ color: t.color }}
          >
            {t.label}
          </span>
          <p className="text-sm text-[#A39E93] leading-relaxed">{t.body}</p>
        </div>
      ))}
    </div>

    <div className="border border-[#D92D20]/40 bg-[#1a0808] p-6">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// THE BAR</div>
      <p className="text-[#E4DFD3] leading-relaxed">
        If a claim cannot survive being clicked through to its source, it does not
        belong on The Archive. That is the bar.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <Link
          to="/legal"
          data-testid="editorial-legal-btn"
          className="inline-flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em]"
        >
          Read the legal notice
        </Link>
        <Link
          to="/submit?mode=correction"
          data-testid="editorial-submit-btn"
          className="inline-flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em]"
        >
          Submit a correction
        </Link>
      </div>
    </div>
  </div>
);

export default EditorialStandards;
